import { useState, useRef, useEffect, useCallback } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { Redirect } from "wouter";
import { Send, Terminal, ImagePlus, X, Copy, Check, Loader2, Bot, User, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem("ayrshire_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

type TextPart = { type: "text"; text: string };
type ImagePart = { type: "image_url"; url: string; mediaType: string };
type MessagePart = TextPart | ImagePart;

interface Message {
  id: string;
  role: "user" | "assistant";
  parts: MessagePart[];
  streaming?: boolean;
}

interface ShellResult {
  stdout: string;
  stderr: string;
  exitCode: number;
}

function renderMarkdown(text: string) {
  const lines = text.split("\n");
  const result: React.ReactNode[] = [];
  let inCode = false;
  let codeLines: string[] = [];
  let codeLang = "";

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.startsWith("```")) {
      if (!inCode) {
        inCode = true;
        codeLang = line.slice(3).trim();
        codeLines = [];
      } else {
        result.push(
          <pre key={i} className="my-2 rounded-lg bg-slate-900 p-3 overflow-x-auto text-xs font-mono text-slate-100 whitespace-pre">
            <div className="text-slate-500 text-[10px] mb-1">{codeLang || "code"}</div>
            {codeLines.join("\n")}
          </pre>
        );
        inCode = false;
        codeLang = "";
        codeLines = [];
      }
      continue;
    }
    if (inCode) { codeLines.push(line); continue; }

    if (line.startsWith("### ")) {
      result.push(<h3 key={i} className="font-semibold text-sm mt-3 mb-1">{line.slice(4)}</h3>);
    } else if (line.startsWith("## ")) {
      result.push(<h2 key={i} className="font-semibold text-base mt-3 mb-1">{line.slice(3)}</h2>);
    } else if (line.startsWith("# ")) {
      result.push(<h1 key={i} className="font-bold text-lg mt-3 mb-1">{line.slice(2)}</h1>);
    } else if (line.startsWith("- ") || line.startsWith("* ")) {
      result.push(<li key={i} className="ml-4 list-disc">{inlineFormat(line.slice(2))}</li>);
    } else if (/^\d+\. /.test(line)) {
      result.push(<li key={i} className="ml-4 list-decimal">{inlineFormat(line.replace(/^\d+\. /, ""))}</li>);
    } else if (line.trim() === "") {
      result.push(<div key={i} className="h-2" />);
    } else {
      result.push(<p key={i} className="leading-relaxed">{inlineFormat(line)}</p>);
    }
  }
  if (inCode && codeLines.length) {
    result.push(
      <pre key="last-code" className="my-2 rounded-lg bg-slate-900 p-3 overflow-x-auto text-xs font-mono text-slate-100 whitespace-pre">
        {codeLines.join("\n")}
      </pre>
    );
  }
  return result;
}

function inlineFormat(text: string): React.ReactNode {
  const parts = text.split(/(`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) return <strong key={i}>{p.slice(2, -2)}</strong>;
    if (p.startsWith("*") && p.endsWith("*")) return <em key={i}>{p.slice(1, -1)}</em>;
    if (p.startsWith("`") && p.endsWith("`")) return <code key={i} className="px-1 py-0.5 rounded bg-slate-100 text-slate-800 text-[0.85em] font-mono">{p.slice(1, -1)}</code>;
    return p;
  });
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { void navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
      className="p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
      title="Copy"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

function MessageBubble({ msg, onCopy }: { msg: Message; onCopy: (t: string) => void }) {
  const isUser = msg.role === "user";
  const textParts = msg.parts.filter((p): p is TextPart => p.type === "text");
  const imageParts = msg.parts.filter((p): p is ImagePart => p.type === "image_url");
  const fullText = textParts.map(p => p.text).join("");

  return (
    <div className={cn("flex gap-3 group", isUser ? "flex-row-reverse" : "flex-row")}>
      <div className={cn(
        "flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center text-white text-sm font-medium mt-1",
        isUser ? "bg-blue-600" : "bg-violet-600"
      )}>
        {isUser ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
      </div>
      <div className={cn("max-w-[80%] space-y-1.5", isUser && "items-end flex flex-col")}>
        {imageParts.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {imageParts.map((p, i) => (
              <img key={i} src={p.url} alt="attached" className="max-h-48 max-w-xs rounded-lg border border-slate-200 object-cover" />
            ))}
          </div>
        )}
        {fullText && (
          <div className={cn(
            "rounded-2xl px-4 py-3 text-sm relative",
            isUser
              ? "bg-blue-600 text-white rounded-tr-sm"
              : "bg-white border border-slate-200 text-slate-800 rounded-tl-sm shadow-sm"
          )}>
            {isUser ? (
              <p className="whitespace-pre-wrap">{fullText}</p>
            ) : (
              <div className="prose prose-sm max-w-none space-y-0.5">
                {renderMarkdown(fullText)}
                {msg.streaming && <span className="inline-block w-1.5 h-4 bg-violet-500 ml-0.5 animate-pulse rounded-sm" />}
              </div>
            )}
            {!isUser && !msg.streaming && fullText && (
              <div className="absolute -bottom-6 right-0 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <CopyButton text={fullText} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ShellWidget() {
  const [cmd, setCmd] = useState("");
  const [result, setResult] = useState<ShellResult | null>(null);
  const [running, setRunning] = useState(false);
  const [copied, setCopied] = useState(false);

  const run = async () => {
    if (!cmd.trim() || running) return;
    setRunning(true);
    setResult(null);
    try {
      const r = await fetch("/api/admin/claude/shell", {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ command: cmd }),
      });
      const d = await r.json() as ShellResult;
      setResult(d);
    } catch {
      setResult({ stdout: "", stderr: "Request failed", exitCode: 1 });
    } finally {
      setRunning(false);
    }
  };

  const out = (result?.stdout || "") + (result?.stderr ? `\n[stderr]\n${result.stderr}` : "");

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 bg-slate-800 text-slate-200">
        <Terminal className="h-3.5 w-3.5" />
        <span className="text-xs font-mono font-medium">Shell — /var/www/projects/ayrshirehub</span>
      </div>
      <div className="flex gap-2 p-2 border-b border-slate-100">
        <span className="text-slate-400 font-mono text-sm self-center shrink-0">$</span>
        <input
          type="text"
          value={cmd}
          onChange={e => setCmd(e.target.value)}
          onKeyDown={e => e.key === "Enter" && void run()}
          placeholder="ls -la | head -20"
          className="flex-1 font-mono text-sm outline-none bg-transparent text-slate-800 placeholder:text-slate-300"
        />
        <button
          onClick={void run}
          disabled={running || !cmd.trim()}
          className="shrink-0 px-3 py-1 rounded-lg bg-slate-800 text-white text-xs font-mono hover:bg-slate-700 disabled:opacity-50 transition-colors"
        >
          {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Run"}
        </button>
      </div>
      {result !== null && (
        <div className="relative">
          <pre className="p-3 text-xs font-mono text-slate-200 bg-slate-900 overflow-x-auto whitespace-pre min-h-[40px] max-h-80 overflow-y-auto">
            {out || "(no output)"}
          </pre>
          <div className="absolute top-2 right-2 flex gap-1">
            {out && (
              <button
                onClick={() => { void navigator.clipboard.writeText(out); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
                className="p-1 rounded bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors"
                title="Copy output"
              >
                {copied ? <Check className="h-3 w-3 text-green-400" /> : <Copy className="h-3 w-3" />}
              </button>
            )}
          </div>
          {result.exitCode !== 0 && (
            <div className="px-3 py-1 bg-red-950 text-red-400 text-xs font-mono">
              exit {result.exitCode}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminClaude() {
  const { user, isLoading } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [images, setImages] = useState<Array<{ url: string; base64: string; mediaType: string }>>([]);
  const [streaming, setStreaming] = useState(false);
  const [model, setModel] = useState("claude-sonnet-4-6");
  const [showShell, setShowShell] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const addImage = (file: File) => {
    const reader = new FileReader();
    reader.onload = e => {
      const dataUrl = e.target?.result as string;
      const [header, base64] = dataUrl.split(",");
      const mediaType = header.match(/data:([^;]+)/)?.[1] ?? "image/jpeg";
      setImages(prev => [...prev, { url: dataUrl, base64, mediaType }]);
    };
    reader.readAsDataURL(file);
  };

  const buildApiMessages = useCallback((): Array<{ role: "user" | "assistant"; content: unknown }> => {
    return messages.map(m => {
      if (m.role === "assistant") {
        return { role: "assistant", content: m.parts.filter(p => p.type === "text").map(p => (p as TextPart).text).join("") };
      }
      const content: unknown[] = [];
      m.parts.forEach(p => {
        if (p.type === "text") content.push({ type: "text", text: p.text });
        if (p.type === "image_url") {
          content.push({ type: "image", source: { type: "base64", media_type: p.mediaType, data: p.url.split(",")[1] } });
        }
      });
      return { role: "user", content };
    });
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if ((!text && images.length === 0) || streaming) return;

    const userParts: MessagePart[] = [];
    images.forEach(img => userParts.push({ type: "image_url", url: img.url, mediaType: img.mediaType }));
    if (text) userParts.push({ type: "text", text });

    const userMsg: Message = { id: crypto.randomUUID(), role: "user", parts: userParts };
    const assistantId = crypto.randomUUID();
    const assistantMsg: Message = { id: assistantId, role: "assistant", parts: [{ type: "text", text: "" }], streaming: true };

    setMessages(prev => [...prev, userMsg, assistantMsg]);
    setInput("");
    setImages([]);
    setStreaming(true);

    const apiMessages = buildApiMessages();
    const userContent: unknown[] = [];
    images.forEach(img => userContent.push({ type: "image", source: { type: "base64", media_type: img.mediaType, data: img.base64 } }));
    if (text) userContent.push({ type: "text", text });
    apiMessages.push({ role: "user", content: userContent.length === 1 && (userContent[0] as {type:string}).type === "text" ? text : userContent });

    abortRef.current = new AbortController();

    try {
      const res = await fetch("/api/admin/claude/chat", {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ messages: apiMessages, model }),
        signal: abortRef.current.signal,
      });

      if (!res.ok || !res.body) {
        const err = await res.text();
        setMessages(prev => prev.map(m => m.id === assistantId
          ? { ...m, parts: [{ type: "text", text: `Error: ${err}` }], streaming: false }
          : m
        ));
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const evt = JSON.parse(line.slice(6)) as { type: string; text?: string; error?: string };
            if (evt.type === "delta" && evt.text) {
              setMessages(prev => prev.map(m => {
                if (m.id !== assistantId) return m;
                const parts = m.parts.slice();
                const last = parts[parts.length - 1];
                if (last?.type === "text") {
                  parts[parts.length - 1] = { type: "text", text: last.text + evt.text! };
                }
                return { ...m, parts };
              }));
            } else if (evt.type === "done" || evt.type === "error") {
              const errText = evt.type === "error" ? `\n\n[Error: ${evt.error}]` : "";
              setMessages(prev => prev.map(m => m.id === assistantId
                ? { ...m, parts: m.parts.map(p => p.type === "text" ? { ...p, text: p.text + errText } : p), streaming: false }
                : m
              ));
            }
          } catch { /* malformed SSE line */ }
        }
      }
    } catch (err: unknown) {
      if ((err as Error)?.name !== "AbortError") {
        setMessages(prev => prev.map(m => m.id === assistantId
          ? { ...m, parts: [{ type: "text", text: "Connection error. Is ANTHROPIC_API_KEY set on the server?" }], streaming: false }
          : m
        ));
      }
    } finally {
      setStreaming(false);
      setMessages(prev => prev.map(m => m.id === assistantId ? { ...m, streaming: false } : m));
    }
  };

  const stop = () => { abortRef.current?.abort(); };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(); }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    for (const item of e.clipboardData.items) {
      if (item.type.startsWith("image/")) {
        const file = item.getAsFile();
        if (file) addImage(file);
      }
    }
  };

  if (isLoading) return null;
  if (user?.role !== "admin") return <Redirect to="/" />;

  return (
    <AdminLayout current="/admin/claude">
      <div className="flex flex-col h-[calc(100vh-80px)] max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between pb-3 border-b border-slate-200 shrink-0">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-700 flex items-center justify-center">
              <Bot className="h-4 w-4 text-white" />
            </div>
            <div>
              <h1 className="font-semibold text-slate-800 leading-none">Claude AI</h1>
              <p className="text-xs text-slate-400 mt-0.5">Admin assistant</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={model}
              onChange={e => setModel(e.target.value)}
              className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-violet-400"
            >
              <option value="claude-sonnet-4-6">Sonnet 4.6</option>
              <option value="claude-haiku-4-5-20251001">Haiku 4.5</option>
              <option value="claude-opus-4-8">Opus 4.8</option>
            </select>
            <button
              onClick={() => setShowShell(s => !s)}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border",
                showShell
                  ? "bg-slate-800 text-white border-slate-800"
                  : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
              )}
            >
              <Terminal className="h-3.5 w-3.5" />
              Shell
            </button>
            {messages.length > 0 && (
              <button
                onClick={() => setMessages([])}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-500 hover:text-slate-700 border border-slate-200 bg-white hover:border-slate-300 transition-colors"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Shell panel */}
        {showShell && (
          <div className="mt-3 shrink-0">
            <ShellWidget />
          </div>
        )}

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto py-4 space-y-6 pr-1">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center gap-4 py-20">
              <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-700 flex items-center justify-center shadow-lg">
                <Bot className="h-8 w-8 text-white" />
              </div>
              <div>
                <h2 className="font-semibold text-slate-700 text-lg">Ask Claude anything</h2>
                <p className="text-slate-400 text-sm mt-1">Text, images, shell results — all in one place</p>
              </div>
              <div className="grid grid-cols-2 gap-2 max-w-md w-full mt-2">
                {[
                  "Summarise today's orders",
                  "Review this code snippet",
                  "Help write a push notification",
                  "Analyse this screenshot",
                ].map(s => (
                  <button
                    key={s}
                    onClick={() => setInput(s)}
                    className="text-left px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-600 hover:border-violet-300 hover:text-violet-700 transition-colors shadow-sm"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map(msg => (
            <MessageBubble key={msg.id} msg={msg} onCopy={t => void navigator.clipboard.writeText(t)} />
          ))}
        </div>

        {/* Input area */}
        <div className="shrink-0 pt-3 border-t border-slate-200">
          {images.length > 0 && (
            <div className="flex gap-2 mb-2 flex-wrap">
              {images.map((img, i) => (
                <div key={i} className="relative group">
                  <img src={img.url} alt="preview" className="h-16 w-16 object-cover rounded-lg border border-slate-200" />
                  <button
                    onClick={() => setImages(prev => prev.filter((_, j) => j !== i))}
                    className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-slate-700 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2 items-end rounded-2xl border border-slate-200 bg-white shadow-sm p-2">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="shrink-0 p-2 rounded-xl hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
              title="Attach image"
            >
              <ImagePlus className="h-5 w-5" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={e => { Array.from(e.target.files ?? []).forEach(addImage); e.target.value = ""; }}
            />
            <textarea
              ref={textareaRef}
              value={input}
              onChange={e => { setInput(e.target.value); e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 200) + "px"; }}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              placeholder="Message Claude… (Shift+Enter for new line, paste images)"
              rows={1}
              className="flex-1 resize-none bg-transparent text-sm text-slate-800 placeholder:text-slate-400 outline-none py-1.5 max-h-48"
            />
            {streaming ? (
              <button
                onClick={stop}
                className="shrink-0 p-2 rounded-xl bg-red-500 hover:bg-red-600 text-white transition-colors"
                title="Stop"
              >
                <X className="h-5 w-5" />
              </button>
            ) : (
              <button
                onClick={() => void send()}
                disabled={!input.trim() && images.length === 0}
                className="shrink-0 p-2 rounded-xl bg-violet-600 hover:bg-violet-700 text-white transition-colors disabled:opacity-40"
                title="Send"
              >
                <Send className="h-5 w-5" />
              </button>
            )}
          </div>
          <p className="text-center text-[10px] text-slate-300 mt-1.5">
            Admin only · shell commands run on the server · never share secrets
          </p>
        </div>
      </div>
    </AdminLayout>
  );
}
